import { Action, Middleware, configureStore } from '@reduxjs/toolkit';
import { Session } from 'application/Shared/types/session';
import { applyMiddleware } from 'redux';

import {
    addUserDataToHeap,
    addUserDataToPendo,
    addUserTrackingScripts
} from '../application/Shared/Utils/UserTracking';
import reducer from '../reducers/index';

/**
 * Interface for the STORE_INFO action
 */
interface StoreInfoAction extends Action {
    type: 'STORE_INFO';
    session?: Session;
}

/**
 * Middleware to handle user properties and tracking
 * @returns {Middleware} Redux middleware function
 */
const userPropertiesMiddleware: Middleware =
    () => (next) => (action: unknown) => {
        if ((action as Action).type === 'STORE_INFO') {
            const storeInfoAction = action as StoreInfoAction;
            const session = storeInfoAction?.session;
            const userTrackingEnabled = session?.tenant?.settings?.userTracking;

            if (userTrackingEnabled !== false) {
                // add heap and pendo script if needed
                addUserTrackingScripts();

                // then add/update user data for heap and pendo
                addUserDataToHeap(session);
                addUserDataToPendo(session);
            }
        }
        next(action);
    };

/**
 * Sets up and configures the Redux store
 * @param {Object} preloadedState - The initial state to preload the store with
 * @returns {ReturnType<typeof configureStore>} Configured Redux store
 */
export const setupStore = (
    preloadedState = {}
): ReturnType<typeof configureStore> =>
    configureStore({
        devTools: process.env.NODE_ENV !== 'production',
        enhancers: (getDefaultEnhancers) =>
            getDefaultEnhancers().concat(
                applyMiddleware(userPropertiesMiddleware)
            ),
        preloadedState,
        reducer
    });
